import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardMedia,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Breadcrumbs,
  MobileStepper
} from '@mui/material';
import {
  DirectionsBoat as BoatIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  ArrowBack as ArrowBackIcon,
  KeyboardArrowLeft,
  KeyboardArrowRight
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { getVesselById } from '../firebase/services/vesselService';
import { Vessel } from '../models/types';
import { useAuth } from '../contexts/AuthContext';

const VesselDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [vessel, setVessel] = useState<Vessel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    const fetchVessel = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const fetchedVessel = await getVesselById(id);
        
        if (!fetchedVessel) {
          setError('Boat not found');
          return;
        }
        
        setVessel(fetchedVessel);
      } catch (err: any) {
        console.error('Error fetching vessel details:', err);
        setError('Failed to load boat details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchVessel();
  }, [id]);
  
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const maxSteps = vessel?.images.length || 0;

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !vessel) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ my: 4 }}>
          {error || 'Boat not found'}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          component={Link}
          to="/vessels"
          sx={{ mt: 2 }}
        >
          Back to All Boats
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
          <Link to="/" style={{ color: 'inherit', textDecoration: 'none' }}>
            Home
          </Link>
          <Link to="/vessels" style={{ color: 'inherit', textDecoration: 'none' }}>
            Boats
          </Link>
          <Typography color="text.primary">{vessel.name}</Typography>
        </Breadcrumbs>

        <Grid container spacing={4}>
          {/* Left column: Images and details */}
          <Grid item xs={12} md={8}>
            <Paper
              elevation={2}
              sx={{
                width: '100%',
                p: 2,
                mb: 4,
                bgcolor: 'background.paper',
                borderRadius: 2,
              }}
            >
              {/* Image carousel */}
              {vessel.images.length > 0 ? (
                <Box sx={{ position: 'relative' }}>
                  <CardMedia
                    component="img"
                    height="400"
                    image={vessel.images[activeStep] || "https://source.unsplash.com/random/?boat"}
                    alt={vessel.name}
                    sx={{ borderRadius: 1 }}
                  />
                  <MobileStepper
                    steps={maxSteps}
                    position="static"
                    activeStep={activeStep}
                    nextButton={
                      <Button
                        size="small"
                        onClick={handleNext}
                        disabled={activeStep === maxSteps - 1}
                      >
                        Next
                        <KeyboardArrowRight />
                      </Button>
                    }
                    backButton={
                      <Button size="small" onClick={handleBack} disabled={activeStep === 0}>
                        <KeyboardArrowLeft />
                        Back
                      </Button>
                    }
                  />
                </Box>
              ) : (
                <CardMedia
                  component="img"
                  height="400"
                  image="https://source.unsplash.com/random/?boat"
                  alt={vessel.name}
                  sx={{ borderRadius: 1 }}
                />
              )}

              {/* Vessel name and type */}
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h4" component="h1">
                  {vessel.name}
                </Typography>
                <Chip
                  label={vessel.type.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}
                  color="primary"
                  icon={<BoatIcon />}
                />
              </Box>

              <Divider sx={{ my: 2 }} />
              
              {/* Description */}
              <Typography variant="body1" paragraph>
                {vessel.description}
              </Typography>
            </Paper>

            {/* Rules section */}
            <Paper
              elevation={2}
              sx={{
                width: '100%',
                p: 2,
                mb: 4,
                bgcolor: 'background.paper',
                borderRadius: 2,
              }}
            >
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <WarningIcon sx={{ mr: 1 }} /> Amenities
              </Typography>
              <List dense>
                {vessel.amenities.map((amenity, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <InfoIcon color="info" />
                    </ListItemIcon>
                    <ListItemText primary={amenity} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>

          {/* Right column: Pricing and booking */}
          <Grid item xs={12} md={4}>
            <Card elevation={3} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
              <Typography variant="h5" gutterBottom>
                Vessel Details
              </Typography>

              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <PeopleIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Capacity"
                    secondary={`${vessel.capacity} people`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <InfoIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Type"
                    secondary={vessel.type.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}
                  />
                </ListItem>
              </List>

              <Divider sx={{ my: 2 }} />

              <Button
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                startIcon={<ScheduleIcon />}
                component={Link}
                to={`/reservation/${vessel.id}`}
                sx={{ mb: 2 }}
              >
                Check Availability & Book
              </Button>

              <Button
                variant="outlined"
                fullWidth
                startIcon={<ArrowBackIcon />}
                component={Link}
                to="/vessels"
              >
                Back to All Boats
              </Button>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h4" component="h1" gutterBottom>
              {vessel.name}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              {vessel.type.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Vessel Details</Typography>
              <Typography><strong>Capacity:</strong> {vessel.capacity} people</Typography>
              <Typography><strong>Manufacturer:</strong> {vessel.manufacturer}</Typography>
              <Typography><strong>Model:</strong> {vessel.model || 'N/A'}</Typography>
              <Typography><strong>Year:</strong> {vessel.year || 'N/A'}</Typography>
              <Typography><strong>Length:</strong> {vessel.length ? `${vessel.length} ft` : 'N/A'}</Typography>
              <Typography><strong>Registration Number:</strong> {vessel.registrationNumber}</Typography>
              <Typography><strong>Date Placed in Service:</strong> {new Date(vessel.datePlacedInService).toLocaleDateString()}</Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Engine Information</Typography>
              <Typography><strong>Engine Horsepower:</strong> {vessel.engineHorsepower} HP</Typography>
              <Typography><strong>Engine Serial Number:</strong> {vessel.engineSerialNumber}</Typography>
            </Paper>
          </Grid>

          {vessel.description && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Description</Typography>
                <Typography>{vessel.description}</Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Box>
    </Container>
  );
};

export default VesselDetailPage; 